import { useState, useEffect } from 'react';
import { Loader2, AlertCircle, MapPin, Truck, Clock, Navigation, SkipForward, CheckCircle, MapPinCheck, Scale, X } from 'lucide-react';
import { transportApi, type CompleteStopRequest, type SkipStopRequest } from '../../services/transport/api';

interface DriverStopInfo {
  stopId: number;
  sequenceOrder: number;
  gaushalaId?: number;
  gaushalaName: string;
  gaushalaAddress?: string;
  gaushalaContactPhone?: string;
  expectedQuantityMinKg?: number;
  expectedQuantityMaxKg?: number;
  estimatedArrivalTime?: string;
  status?: 'PENDING' | 'ARRIVED' | 'COMPLETED' | 'SKIPPED';
  partitionLabel?: string;
}

interface DriverRoute {
  routeId: number;
  routeCode: string;
  routeName: string;
  routeStartTime?: string;
  scheduleTypeName?: string;
  pickupStops: DriverStopInfo[];
  dropPointName?: string;
  dropPointAddress?: string;
  vehicleNumber?: string;
  vehicleType?: string;
  vehicleCapacityKg?: number;
  totalDistanceKm?: number;
  estimatedDurationMin?: number;
  statusCode: string;
  statusName: string;
  executionId?: number;
  executionStatus?: string;
}

// Stop status colors and labels
const stopStatusConfig: Record<string, { bg: string; text: string; label: string }> = {
  PENDING: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Pending' },
  ARRIVED: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Arrived' },
  COMPLETED: { bg: 'bg-green-100', text: 'text-green-700', label: 'Completed' },
  SKIPPED: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'Skipped' },
};

const statusColors: Record<string, { bg: string; text: string }> = {
  DRAFT: { bg: 'bg-gray-100', text: 'text-gray-800' },
  ACTIVE: { bg: 'bg-green-100', text: 'text-green-800' },
  PAUSED: { bg: 'bg-yellow-100', text: 'text-yellow-800' },
  COMPLETED: { bg: 'bg-blue-100', text: 'text-blue-800' },
  CANCELLED: { bg: 'bg-red-100', text: 'text-red-800' }
};

// Skip reason options
const skipReasons = [
  'Material not available',
  'Gaushala closed',
  'Access issue',
  'Weather conditions',
  'Vehicle breakdown',
  'Other'
];

export default function DriverRoutesPage() {
  const [routes, setRoutes] = useState<DriverRoute[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<Record<string, boolean>>({});
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Complete stop dialog state
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [completeDialogData, setCompleteDialogData] = useState<{
    executionId: number;
    stopId: number;
    gaushalaName: string;
    expectedMin?: number;
    expectedMax?: number;
  } | null>(null);
  const [quantityCollected, setQuantityCollected] = useState('');

  // Skip stop dialog state
  const [showSkipDialog, setShowSkipDialog] = useState(false);
  const [skipDialogData, setSkipDialogData] = useState<{
    executionId: number;
    stopId: number;
    gaushalaName: string;
  } | null>(null);
  const [skipReason, setSkipReason] = useState('');
  const [skipNotes, setSkipNotes] = useState('');

  const fetchRoutes = async () => {
    try {
      setLoading(true);
      const data = await transportApi.getDriverRoutes();
      setRoutes(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load routes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoutes();
  }, []);

  // Clear success message after 3 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Handle Mark Arrived
  const handleMarkArrived = async (executionId: number, stopId: number, gaushalaName: string) => {
    const key = `arrived-${executionId}-${stopId}`;
    setActionLoading(prev => ({ ...prev, [key]: true }));
    setError(null);

    try {
      await transportApi.markStopArrived(executionId, stopId);
      setSuccessMessage(`Marked as arrived at ${gaushalaName}`);
      // Refresh routes to get updated status
      await fetchRoutes();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to mark as arrived');
    } finally {
      setActionLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  // Open Complete Dialog
  const openCompleteDialog = (executionId: number, stopId: number, gaushalaName: string, expectedMin?: number, expectedMax?: number) => {
    setCompleteDialogData({ executionId, stopId, gaushalaName, expectedMin, expectedMax });
    setQuantityCollected('');
    setShowCompleteDialog(true);
  };

  // Handle Complete Stop
  const handleCompleteStop = async () => {
    if (!completeDialogData || !quantityCollected) return;

    const { executionId, stopId, gaushalaName } = completeDialogData;
    const key = `complete-${executionId}-${stopId}`;
    setActionLoading(prev => ({ ...prev, [key]: true }));
    setError(null);

    try {
      const request: CompleteStopRequest = {
        quantityCollectedKg: parseFloat(quantityCollected)
      };
      await transportApi.completeStop(executionId, stopId, request);
      setSuccessMessage(`Completed collection at ${gaushalaName} - ${quantityCollected} kg`);
      setShowCompleteDialog(false);
      setCompleteDialogData(null);
      // Refresh routes
      await fetchRoutes();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to complete stop');
    } finally {
      setActionLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  // Open Skip Dialog
  const openSkipDialog = (executionId: number, stopId: number, gaushalaName: string) => {
    setSkipDialogData({ executionId, stopId, gaushalaName });
    setSkipReason('');
    setSkipNotes('');
    setShowSkipDialog(true);
  };

  // Handle Skip Stop
  const handleSkipStop = async () => {
    if (!skipDialogData || !skipReason) return;

    const { executionId, stopId, gaushalaName } = skipDialogData;
    const key = `skip-${executionId}-${stopId}`;
    setActionLoading(prev => ({ ...prev, [key]: true }));
    setError(null);

    try {
      const request: SkipStopRequest = {
        skipReason,
        notes: skipNotes || undefined
      };
      await transportApi.skipStop(executionId, stopId, request);
      setSuccessMessage(`Skipped ${gaushalaName}`);
      setShowSkipDialog(false);
      setSkipDialogData(null);
      // Refresh routes
      await fetchRoutes();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to skip stop');
    } finally {
      setActionLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  // Check if stop actions are available (execution is active)
  const canPerformStopActions = (route: DriverRoute): boolean => {
    return !!route.executionId && ['STARTED', 'IN_PROGRESS'].includes(route.executionStatus || '');
  };

  // Get button loading state
  const isButtonLoading = (action: string, executionId: number, stopId: number): boolean => {
    return actionLoading[`${action}-${executionId}-${stopId}`] || false;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">My Routes</h2>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <div className="flex gap-3">
            <AlertCircle className="h-5 w-5 text-red-600" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        </div>
      )}

      {routes.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-white p-8 text-center">
          <p className="text-gray-600">No routes assigned to you</p>
        </div>
      ) : (
        <div className="space-y-4">
          {routes.map((route) => {
            const statusColor = statusColors[route.statusCode] || statusColors.DRAFT;
            return (
              <div key={route.routeId} className="rounded-lg border border-gray-200 bg-white p-6">
                {/* Route Header */}
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{route.routeName}</h3>
                    <p className="text-sm text-gray-600">{route.routeCode}</p>
                  </div>
                  <span className={`inline-flex items-center rounded-full ${statusColor.bg} ${statusColor.text} px-3 py-1 text-xs font-medium`}>
                    {route.statusName}
                  </span>
                </div>

                {/* Route Info Grid */}
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 mb-4">
                  {route.routeStartTime && (
                    <div className="flex items-center gap-2">
                      <Clock size={16} className="text-blue-600" />
                      <div>
                        <p className="text-xs text-gray-500">Start Time</p>
                        <p className="text-sm font-medium text-gray-900">{route.routeStartTime}</p>
                      </div>
                    </div>
                  )}
                  {route.vehicleNumber && (
                    <div className="flex items-center gap-2">
                      <Truck size={16} className="text-blue-600" />
                      <div>
                        <p className="text-xs text-gray-500">Vehicle</p>
                        <p className="text-sm font-medium text-gray-900">{route.vehicleNumber}</p>
                        {route.vehicleCapacityKg && (
                          <p className="text-xs text-gray-500">{route.vehicleCapacityKg} kg capacity</p>
                        )}
                      </div>
                    </div>
                  )}
                  {route.totalDistanceKm && (
                    <div className="flex items-center gap-2">
                      <Navigation size={16} className="text-blue-600" />
                      <div>
                        <p className="text-xs text-gray-500">Distance</p>
                        <p className="text-sm font-medium text-gray-900">{route.totalDistanceKm.toFixed(1)} km</p>
                      </div>
                    </div>
                  )}
                  {route.estimatedDurationMin && (
                    <div className="flex items-center gap-2">
                      <Clock size={16} className="text-gray-500" />
                      <div>
                        <p className="text-xs text-gray-500">Est. Duration</p>
                        <p className="text-sm font-medium text-gray-900">{route.estimatedDurationMin} min</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Pickup Stops */}
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Stops ({route.pickupStops?.length || 0})</h4>
                  <div className="space-y-3">
                    {route.pickupStops?.map((stop) => {
                      const stopStatus = stop.status || 'PENDING';
                      const statusConfig = stopStatusConfig[stopStatus] || stopStatusConfig.PENDING;
                      const isActionable = canPerformStopActions(route) && stopStatus === 'PENDING';
                      const isArrived = stopStatus === 'ARRIVED';
                      const isCompleted = stopStatus === 'COMPLETED';
                      const isSkipped = stopStatus === 'SKIPPED';

                      return (
                        <div key={stop.stopId} className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                          {/* Stop Header */}
                          <div className="flex items-start gap-3 mb-3">
                            <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-blue-600 text-white text-sm font-bold">
                              {stop.sequenceOrder}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="font-semibold text-gray-900">{stop.gaushalaName}</p>
                                  <p className="text-xs text-gray-500">ID: {stop.gaushalaId || stop.stopId}</p>
                                </div>
                                <div className="flex items-center gap-1.5">
                                  {stop.partitionLabel && (
                                    <span className="inline-flex items-center gap-1 rounded-full bg-blue-600 text-white px-2.5 py-0.5 text-xs font-medium">
                                      <Truck size={12} />
                                      {stop.partitionLabel}
                                    </span>
                                  )}
                                  <span className={`inline-flex items-center rounded-full ${statusConfig.bg} ${statusConfig.text} px-2.5 py-0.5 text-xs font-medium`}>
                                    {statusConfig.label}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Stop Details */}
                          <div className="ml-11 space-y-1 mb-3">
                            {stop.gaushalaAddress && (
                              <div className="flex items-center gap-1.5 text-xs text-gray-600">
                                <MapPin size={12} className="text-gray-400" />
                                <span>{stop.gaushalaAddress}</span>
                              </div>
                            )}
                            {stop.gaushalaContactPhone && (
                              <p className="text-xs text-gray-500">Contact: {stop.gaushalaContactPhone}</p>
                            )}
                            {(stop.expectedQuantityMinKg || stop.expectedQuantityMaxKg) && (
                              <div className="flex items-center gap-1.5 text-xs text-gray-600">
                                <Scale size={12} className="text-gray-400" />
                                <span>Expected: {stop.expectedQuantityMinKg || 0} - {stop.expectedQuantityMaxKg || 0} kg</span>
                              </div>
                            )}
                            {stop.estimatedArrivalTime && (
                              <div className="flex items-center gap-1.5 text-xs text-blue-600">
                                <Clock size={12} />
                                <span>ETA: {stop.estimatedArrivalTime}</span>
                              </div>
                            )}
                          </div>

                          {/* Action Buttons - Always show all three buttons */}
                          <div className="ml-11 pt-3 border-t border-gray-100">
                            <div className="flex flex-wrap gap-2">
                              {/* Skip Button */}
                              <button
                                onClick={() => openSkipDialog(route.executionId || route.routeId, stop.stopId, stop.gaushalaName)}
                                disabled={isButtonLoading('skip', route.executionId || route.routeId, stop.stopId) || isCompleted || isSkipped}
                                className="inline-flex items-center gap-1.5 rounded-md border border-orange-300 bg-white px-3 py-1.5 text-xs font-medium text-orange-600 hover:bg-orange-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                aria-label={`Skip ${stop.gaushalaName}`}
                                tabIndex={0}
                              >
                                {isButtonLoading('skip', route.executionId || route.routeId, stop.stopId) ? (
                                  <Loader2 size={14} className="animate-spin" />
                                ) : (
                                  <SkipForward size={14} />
                                )}
                                Skip
                              </button>

                              {/* Arrived Button */}
                              <button
                                onClick={() => handleMarkArrived(route.executionId || route.routeId, stop.stopId, stop.gaushalaName)}
                                disabled={isButtonLoading('arrived', route.executionId || route.routeId, stop.stopId) || isArrived || isCompleted || isSkipped}
                                className="inline-flex items-center gap-1.5 rounded-md border border-blue-300 bg-white px-3 py-1.5 text-xs font-medium text-blue-600 hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                aria-label={`Mark arrived at ${stop.gaushalaName}`}
                                tabIndex={0}
                              >
                                {isButtonLoading('arrived', route.executionId || route.routeId, stop.stopId) ? (
                                  <Loader2 size={14} className="animate-spin" />
                                ) : (
                                  <MapPinCheck size={14} />
                                )}
                                Arrived
                              </button>

                              {/* Complete Button */}
                              <button
                                onClick={() => openCompleteDialog(
                                  route.executionId || route.routeId,
                                  stop.stopId,
                                  stop.gaushalaName,
                                  stop.expectedQuantityMinKg,
                                  stop.expectedQuantityMaxKg
                                )}
                                disabled={isButtonLoading('complete', route.executionId || route.routeId, stop.stopId) || isCompleted || isSkipped}
                                className="inline-flex items-center gap-1.5 rounded-md bg-green-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                aria-label={`Complete collection at ${stop.gaushalaName}`}
                                tabIndex={0}
                              >
                                {isButtonLoading('complete', route.executionId || route.routeId, stop.stopId) ? (
                                  <Loader2 size={14} className="animate-spin" />
                                ) : (
                                  <CheckCircle size={14} />
                                )}
                                Complete
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Drop Point */}
                {route.dropPointName && (
                  <div className="rounded-lg border border-green-200 bg-green-50 p-3">
                    <div className="flex items-center gap-2">
                      <MapPin size={16} className="text-green-600" />
                      <div>
                        <p className="text-xs text-green-700">Drop-off Point (Manufacturing Unit)</p>
                        <p className="text-sm font-medium text-green-900">{route.dropPointName}</p>
                        {route.dropPointAddress && <p className="text-xs text-green-700">{route.dropPointAddress}</p>}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Success Message Toast */}
      {successMessage && (
        <div className="fixed bottom-4 right-4 z-50 rounded-lg border border-green-200 bg-green-50 px-4 py-3 shadow-lg">
          <div className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-600" />
            <p className="text-sm font-medium text-green-800">{successMessage}</p>
          </div>
        </div>
      )}

      {/* Complete Stop Dialog */}
      {showCompleteDialog && completeDialogData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowCompleteDialog(false)} />
          <div className="relative z-10 w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Complete Collection</h3>
              <button
                onClick={() => setShowCompleteDialog(false)}
                className="rounded-lg p-1 hover:bg-gray-100"
                aria-label="Close dialog"
              >
                <X size={20} className="text-gray-500" />
              </button>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                Recording collection at <span className="font-medium">{completeDialogData.gaushalaName}</span>
              </p>
              {(completeDialogData.expectedMin || completeDialogData.expectedMax) && (
                <p className="text-xs text-gray-500">
                  Expected: {completeDialogData.expectedMin || 0} - {completeDialogData.expectedMax || 0} kg
                </p>
              )}
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Quantity Collected (kg) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                value={quantityCollected}
                onChange={(e) => setQuantityCollected(e.target.value)}
                placeholder="Enter quantity in kg"
                min="0"
                step="0.1"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                autoFocus
              />
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowCompleteDialog(false)}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCompleteStop}
                disabled={!quantityCollected || parseFloat(quantityCollected) <= 0 || isButtonLoading('complete', completeDialogData.executionId, completeDialogData.stopId)}
                className="rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isButtonLoading('complete', completeDialogData.executionId, completeDialogData.stopId) ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <CheckCircle size={16} />
                )}
                Complete Collection
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Skip Stop Dialog */}
      {showSkipDialog && skipDialogData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowSkipDialog(false)} />
          <div className="relative z-10 w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Skip Stop</h3>
              <button
                onClick={() => setShowSkipDialog(false)}
                className="rounded-lg p-1 hover:bg-gray-100"
                aria-label="Close dialog"
              >
                <X size={20} className="text-gray-500" />
              </button>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-600">
                Skipping <span className="font-medium">{skipDialogData.gaushalaName}</span>
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reason for Skipping <span className="text-red-500">*</span>
              </label>
              <select
                value={skipReason}
                onChange={(e) => setSkipReason(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
              >
                <option value="">Select a reason</option>
                {skipReasons.map((reason) => (
                  <option key={reason} value={reason}>{reason}</option>
                ))}
              </select>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Additional Notes (optional)
              </label>
              <textarea
                value={skipNotes}
                onChange={(e) => setSkipNotes(e.target.value)}
                placeholder="Add any additional details..."
                rows={3}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none resize-none"
              />
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowSkipDialog(false)}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSkipStop}
                disabled={!skipReason || isButtonLoading('skip', skipDialogData.executionId, skipDialogData.stopId)}
                className="rounded-lg bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isButtonLoading('skip', skipDialogData.executionId, skipDialogData.stopId) ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <SkipForward size={16} />
                )}
                Skip Stop
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
